import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
import "./styles/global.css";
import { config } from "./config";
import { ClerkProvider } from "@clerk/clerk-react";
import { BrowserRouter, useLocation, useNavigate } from "react-router-dom";
import { AppResourcesProvider } from "./contexts/AppResourcesContext";
import { UserProfileProvider } from "./contexts/UserProfileContext";
import { AlertQueueProvider } from "./contexts/AlertQueueContext";
import { ResourceCelebrationProvider } from "./contexts/ResourceCelebrationContext";
import { MilestoneProvider } from "./contexts/MilestoneContext";
import * as serviceWorkerRegistration from "./serviceWorkerRegistration";
import { loadBrowserClerkLocalization } from "./clerkLocalizationLoader";
import { maskToLastChars } from "./utils/maskToLastChars";
import { preserveAuthReturnQuery } from "./utils/authReturnPath";

const shouldMaskSensitiveLogs = String(config.ENVIRONMENT || "").toLowerCase() === "production";
const displaySensitiveConfig = (value) => (shouldMaskSensitiveLogs ? maskToLastChars(value, 6) : value);

// Log environment variables at startup
console.info("NODE_ENV:", process.env.NODE_ENV);
console.info("ENVIRONMENT:", config.ENVIRONMENT);
console.info("API_BASE_URL:", config.API_BASE_URL);
console.info("GIT_SHA:", config.GIT_SHA);
console.info("GA_MEASUREMENT_ID:", displaySensitiveConfig(config.GA_MEASUREMENT_ID));
console.info("POSTHOG_HOST:", config.POSTHOG_HOST);
console.info("POSTHOG_PROJECT_TOKEN:", displaySensitiveConfig(config.POSTHOG_PROJECT_TOKEN));
console.info("POSTHOG_ENABLED:", config.POSTHOG_ENABLED);
console.info("CLERK_PUBLISHABLE_KEY:", displaySensitiveConfig(config.CLERK_PUBLISHABLE_KEY));

// Use createRoot to render the application
const root = ReactDOM.createRoot(document.getElementById("root"));
const hasClerkKey = Boolean(config.CLERK_PUBLISHABLE_KEY);

if (!hasClerkKey) {
  console.warn("CLERK_PUBLISHABLE_KEY is not set. Rendering app without Clerk.");
}

// Admin portal doesn't need AlertQueueProvider (only supports GettonciniProvider)
// but does need UserProfileProvider for user info in drawer menu, etc.
const isAdminPortal = process.env.REACT_APP_IS_ADMIN_PORTAL === 'true';

const AppWithProviders = isAdminPortal ? (
  <AppResourcesProvider>
    <UserProfileProvider>
      <App />
    </UserProfileProvider>
  </AppResourcesProvider>
) : (
  <AlertQueueProvider>
    <AppResourcesProvider>
      <ResourceCelebrationProvider>
        <MilestoneProvider>
          <UserProfileProvider>
            <App />
          </UserProfileProvider>
        </MilestoneProvider>
      </ResourceCelebrationProvider>
    </AppResourcesProvider>
  </AlertQueueProvider>
);

const RoutedClerkProvider = ({ clerkLocalization }) => {
  const navigate = useNavigate();
  const location = useLocation();

  if (!hasClerkKey) {
    return AppWithProviders;
  }

  return (
    <ClerkProvider
      publishableKey={config.CLERK_PUBLISHABLE_KEY}
      localization={clerkLocalization}
      afterSignOutUrl="/"
      signInUrl="/signin"
      signUpUrl="/signup"
      signInFallbackRedirectUrl="/"
      signUpFallbackRedirectUrl="/"
      routerPush={(to) => navigate(preserveAuthReturnQuery(to, location))}
      routerReplace={(to) => navigate(preserveAuthReturnQuery(to, location), { replace: true })}
    >
      {AppWithProviders}
    </ClerkProvider>
  );
};

const renderApp = (clerkLocalization) => {
  root.render(
    <React.StrictMode>
      <BrowserRouter>
        <RoutedClerkProvider clerkLocalization={clerkLocalization} />
      </BrowserRouter>
    </React.StrictMode>
  );
};

async function bootstrap() {
  if (!hasClerkKey) {
    renderApp(undefined);
    return;
  }

  const { code, localization } = await loadBrowserClerkLocalization();
  if (code) {
    console.info("CLERK_LOCALIZATION:", code);
  }

  renderApp(localization);
}

bootstrap().catch((error) => {
  console.warn("Failed to bootstrap Clerk localization, rendering without custom localization.", error);
  renderApp(undefined);
});

// Register service worker for PWA installability (no caching)
serviceWorkerRegistration.register();
